'use client'

import { useState, useEffect } from 'react'
import { Search, Plus, Edit, Copy, Trash2, Eye } from 'lucide-react'

interface PrintZone {
  id: string
  zone_type: 'text' | 'qr' | 'barcode' | 'image' | 'line' | 'box'
  zone_name: string | null
  x: number
  y: number
  width: number
  height: number
  field_mapping: string | null
  static_content: string | null
}

interface PrintTemplate {
  id: number
  name: string
  description: string | null
  template_type: string
  width: number
  height: number
  scenario: 'item' | 'location' | 'shelf' | 'tote' | 'quantity_update' | null
  category_id: string | null
  category_name: string | null
  canvas_rotation: number
  is_default: boolean
  orientation: 'portrait' | 'landscape'
  active: boolean
  zones: PrintZone[] | null | undefined
  created_at: string
  updated_at: string
}

interface TemplateLibraryProps {
  onSelectTemplate: (template: PrintTemplate) => void
  onCreateNew: () => void
}

const scenarioColors = {
  item: '#FF6B35',      // Orange
  location: '#00F0FF',  // Cyan
  shelf: '#B91BFF',     // Purple
  tote: '#FFD93D',      // Yellow
  quantity_update: '#6BCF7F' // Green
}

const scenarioLabels = {
  item: 'Item',
  location: 'Location',
  shelf: 'Shelf',
  tote: 'Tote',
  quantity_update: 'Qty Update'
}

export default function TemplateLibrary({ onSelectTemplate, onCreateNew }: TemplateLibraryProps) {
  const [templates, setTemplates] = useState<PrintTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [scenarioFilter, setScenarioFilter] = useState<string>('all')

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/templates-pg')
      const result = await response.json()
      if (result.success) {
        setTemplates(result.data)
      }
    } catch (error) {
      console.error('Error fetching templates:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDuplicate = async (template: PrintTemplate) => {
    try {
      const newTemplate = {
        ...template,
        name: `${template.name} (Copy)`,
        is_default: false,
        zones: (template.zones || []).map(zone => ({
          zone_type: zone.zone_type,
          zone_name: zone.zone_name,
          x: zone.x,
          y: zone.y,
          width: zone.width,
          height: zone.height,
          field_mapping: zone.field_mapping,
          static_content: zone.static_content
        }))
      }

      const response = await fetch('/api/templates-pg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTemplate)
      })

      if (response.ok) {
        fetchTemplates()
      }
    } catch (error) {
      console.error('Error duplicating template:', error)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this template?')) return

    try {
      const response = await fetch(`/api/templates-pg/${id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        fetchTemplates()
      }
    } catch (error) {
      console.error('Error deleting template:', error)
    }
  }

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesScenario = scenarioFilter === 'all' || template.scenario === scenarioFilter
    return matchesSearch && matchesScenario
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-white/60">Loading templates...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-black/30 border border-white/20 rounded-lg
                     text-white placeholder-white/40 focus:outline-none focus:border-[#00F0FF]
                     focus:shadow-[0_0_10px_rgba(0,240,255,0.2)] transition-all"
          />
        </div>

        <button
          onClick={onCreateNew}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-bold uppercase tracking-wide
                   bg-gradient-to-br from-[#FFD93D] to-[#FFA500] text-[#1A1F2E]
                   shadow-[0_0_20px_rgba(255,217,61,0.4)] hover:shadow-[0_0_30px_rgba(255,217,61,0.6)]
                   transition-all duration-200"
        >
          <Plus className="w-5 h-5" />
          <span>New Template</span>
        </button>
      </div>

      {/* Scenario Filters */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setScenarioFilter('all')}
          className={`px-4 py-2 rounded-lg font-semibold uppercase tracking-wide text-sm transition-all
                   ${scenarioFilter === 'all'
                     ? 'bg-white/20 text-white'
                     : 'bg-black/20 text-white/60 hover:bg-black/30'
                   }`}
        >
          All
        </button>
        {Object.entries(scenarioLabels).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setScenarioFilter(key)}
            className={`px-4 py-2 rounded-lg font-semibold uppercase tracking-wide text-sm transition-all
                     ${scenarioFilter === key
                       ? 'text-white'
                       : 'bg-black/20 text-white/60 hover:bg-black/30'
                     }`}
            style={scenarioFilter === key ? {
              backgroundColor: `${scenarioColors[key as keyof typeof scenarioColors]}20`,
              borderColor: scenarioColors[key as keyof typeof scenarioColors],
              borderWidth: '2px'
            } : {}}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredTemplates.map((template) => (
          <div
            key={template.id}
            className="bg-gradient-to-br from-[#0F172A]/95 to-[#1E293B]/95 rounded-xl
                     border-2 p-4 transition-all duration-300 hover:scale-[1.02]"
            style={{
              borderColor: template.scenario ? scenarioColors[template.scenario] : '#94A3B8',
              boxShadow: template.scenario
                ? `0 0 20px ${scenarioColors[template.scenario]}40`
                : '0 0 20px rgba(148, 163, 184, 0.2)'
            }}
          >
            {/* Preview */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-3 mb-3 aspect-[2/3] flex items-center justify-center relative overflow-hidden">
              <div
                style={{
                  width: template.orientation === 'portrait' ? '65%' : '85%',
                  height: template.orientation === 'portrait' ? '85%' : '65%',
                  position: 'relative',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #D1D5DB',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}
              >
                {/* Render zones preview */}
                {(template.zones || []).slice(0, 5).map((zone, idx) => (
                  <div
                    key={idx}
                    className="absolute"
                    style={{
                      left: `${(Number(zone.x) / Number(template.width)) * 100}%`,
                      top: `${(Number(zone.y) / Number(template.height)) * 100}%`,
                      width: `${(Number(zone.width) / Number(template.width)) * 100}%`,
                      height: `${(Number(zone.height) / Number(template.height)) * 100}%`
                    }}
                  >
                    {zone.zone_type === 'qr' && (
                      <div className="w-full h-full bg-black flex items-center justify-center text-white text-[10px] font-bold border border-black">
                        QR
                      </div>
                    )}
                    {zone.zone_type === 'barcode' && (
                      <div className="w-full h-full bg-white border border-black flex items-center justify-center px-1">
                        <div className="flex gap-[1px] h-full items-center">
                          {[...Array(12)].map((_, i) => (
                            <div key={i} className="w-[1px] h-[70%] bg-black" />
                          ))}
                        </div>
                      </div>
                    )}
                    {zone.zone_type === 'text' && (
                      <div className="w-full h-full border border-black/40 bg-gray-100 flex items-center justify-center px-1">
                        <div className="text-[8px] text-black font-semibold truncate w-full text-center">
                          {zone.field_mapping || 'Text'}
                        </div>
                      </div>
                    )}
                    {zone.zone_type === 'image' && (
                      <div className="w-full h-full border-2 border-dashed border-gray-400 bg-gray-50 flex items-center justify-center">
                        <div className="text-[8px] text-gray-400">IMG</div>
                      </div>
                    )}
                    {zone.zone_type === 'line' && (
                      <div className="w-full h-full bg-black"></div>
                    )}
                    {zone.zone_type === 'box' && (
                      <div className="w-full h-full border-2 border-black"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Info */}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-bold text-white">{template.name}</h3>
                {template.is_default && (
                  <span className="px-2 py-1 rounded text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/40">
                    ★ Default
                  </span>
                )}
              </div>

              {template.description && (
                <p className="text-sm text-white/60">{template.description}</p>
              )}

              <div className="flex items-center gap-3 text-xs text-white/60">
                <span>📐 {Number(template.width).toFixed(1)}" × {Number(template.height).toFixed(1)}"</span>
                <span>📝 {template.orientation}</span>
              </div>

              {template.scenario && (
                <div className="flex items-center gap-2">
                  <span
                    className="px-3 py-1 rounded-full text-xs font-bold"
                    style={{
                      backgroundColor: `${scenarioColors[template.scenario]}20`,
                      color: scenarioColors[template.scenario],
                      border: `1px solid ${scenarioColors[template.scenario]}60`
                    }}
                  >
                    {scenarioLabels[template.scenario]}
                  </span>
                  {template.category_name && (
                    <span className="text-xs text-white/60">→ {template.category_name}</span>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => onSelectTemplate(template)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg
                           bg-gradient-to-br from-[#00F0FF] to-[#00B8D4] text-white font-bold
                           hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDuplicate(template)}
                  className="px-4 py-2 rounded-lg bg-black/30 border border-white/20 text-white/80
                           hover:bg-black/40 hover:border-white/40 transition-all"
                  title="Duplicate"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="px-4 py-2 rounded-lg bg-black/30 border border-red-500/40 text-red-400
                           hover:bg-red-500/20 hover:border-red-500/60 transition-all"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-20">
          <div className="text-white/40 text-lg mb-4">No templates found</div>
          <button
            onClick={onCreateNew}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold uppercase
                     bg-gradient-to-br from-[#00F0FF] to-[#00B8D4] text-white
                     shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)]
                     transition-all"
          >
            <Plus className="w-5 h-5" />
            <span>Create Your First Template</span>
          </button>
        </div>
      )}
    </div>
  )
}
